import React from 'react';
import Navbar from '../components/Navbar';
import { useAuth } from '../lib/auth';
import { AliasBadge, TrustBadge, VerificationBadge } from '../components/Badges';
import { Card } from '../components/ui/card';
import { Mail, Phone, Building2, Shield, Eye, EyeOff } from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Profile</h1>
        <p className="text-sm text-gray-600 mt-1">Your public alias and private identity.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {/* Public Profile */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-100">
              <Eye className="w-4 h-4 text-gray-400" />
              <span className="text-xs uppercase tracking-wider text-gray-500">Public Profile</span>
            </div>
            <AliasBadge alias={user.alias} />
            <h2 className="font-heading text-xl font-semibold mt-4">
              {user.role === 'buyer' ? 'Verified Buyer' : user.role === 'seller' ? 'Verified Seller' : 'Admin'}
            </h2>
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Trust Score</span>
                <TrustBadge score={user.trust_score} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Verification</span>
                <VerificationBadge level={user.verification_level} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Member Since</span>
                <span className="text-sm font-mono">{new Date(user.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-500">
              This is what other marketplace users see. Your real identity is never exposed.
            </div>
          </Card>

          {/* Private Profile */}
          <Card className="p-6 border-amber-200 bg-amber-50/30">
            <div className="flex items-center gap-2 mb-4 pb-4 border-b border-amber-200">
              <EyeOff className="w-4 h-4 text-amber-700" />
              <span className="text-xs uppercase tracking-wider text-amber-800 font-medium">Private (Held by LeadXchange)</span>
            </div>
            <div className="space-y-4">
              <div>
                <div className="text-xs text-gray-500">Name</div>
                <div className="text-sm font-medium text-gray-900 mt-0.5">{user.name}</div>
              </div>
              {user.company_name && (
                <div className="flex gap-3">
                  <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Company</div>
                    <div className="text-sm font-medium">{user.company_name}</div>
                  </div>
                </div>
              )}
              {user.abn && (
                <div className="flex gap-3">
                  <Shield className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">ABN</div>
                    <div className="text-sm font-mono">{user.abn}</div>
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <Mail className="w-4 h-4 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <div className="text-sm">{user.email}</div>
                </div>
              </div>
              {user.phone && (
                <div className="flex gap-3">
                  <Phone className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-xs text-gray-500">Phone</div>
                    <div className="text-sm font-mono">{user.phone}</div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
